import {updateSearchParams} from '@github-ui/history'
import {announce} from '@github-ui/aria-live'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useCallback, useEffect, useMemo, useRef, useSyncExternalStore} from 'react'
import {isNeedsAttention} from './entity-helpers'
import type {Finding} from './types'
import type {FindingsView} from './use-filter-findings'

const FINDING_QUERY_PARAM = 'finding'
const LINKED_FINDING_ANNOUNCEMENT =
  'Showing linked finding. Filters and the sidebar are temporarily hidden. Use Show all findings to return to all findings.'

export const getFindingToggleId = (findingId: string) => `copilot-finding-${encodeURIComponent(findingId)}`

interface FocusFindingToggleOptions {
  announcement?: string
  fallbackToContainer?: boolean
}

const focusFindingToggle = (
  findingId: string,
  {announcement, fallbackToContainer = false}: FocusFindingToggleOptions = {},
) => {
  let attempts = 0
  let animationFrame = requestAnimationFrame(function focusFinding() {
    const toggle = document.getElementById(getFindingToggleId(findingId))
    if (toggle instanceof HTMLButtonElement) {
      toggle.scrollIntoView({block: 'center'})
      toggle.focus({preventScroll: true})
      if (announcement) announce(announcement)
      return
    }

    attempts += 1
    if (attempts < 10) {
      animationFrame = requestAnimationFrame(focusFinding)
    } else if (fallbackToContainer) {
      const focusTarget = document.getElementById('findings-heading') ?? document.getElementById('findings-content')
      focusTarget?.focus({preventScroll: true})
    }
  })

  return () => cancelAnimationFrame(animationFrame)
}

export const focusFindingAfterStatusAction = (findingId: string) =>
  focusFindingToggle(findingId, {fallbackToContainer: true})

const subscribeToLocation = (onStoreChange: () => void) => {
  window.addEventListener('popstate', onStoreChange)
  window.addEventListener('statechange', onStoreChange)
  window.addEventListener('turbo:load', onStoreChange)
  window.navigation?.addEventListener('currententrychange', onStoreChange)

  return () => {
    window.removeEventListener('popstate', onStoreChange)
    window.removeEventListener('statechange', onStoreChange)
    window.removeEventListener('turbo:load', onStoreChange)
    window.navigation?.removeEventListener('currententrychange', onStoreChange)
  }
}

const getLocationSearch = () => window.location.search
const getServerLocationSearch = () => ssrSafeLocation.search
const focusFindingsContainer = () => {
  requestAnimationFrame(() => {
    const focusTarget = document.getElementById('findings-heading') ?? document.getElementById('findings-content')
    focusTarget?.focus({preventScroll: true})
  })
}

interface UseFindingDeepLinkOptions {
  openFindings: Finding[]
  ignoredFindings: Finding[]
  outdatedFindings: Finding[]
  onExpandFinding: (findingId: string) => void
  enabled: boolean
}

interface ProcessedFinding {
  findingId: string
  view: FindingsView
}

export const useFindingDeepLink = ({
  openFindings,
  ignoredFindings,
  outdatedFindings,
  onExpandFinding,
  enabled,
}: UseFindingDeepLinkOptions) => {
  const processedFindingRef = useRef<ProcessedFinding | null>(null)
  const locationSearch = useSyncExternalStore(subscribeToLocation, getLocationSearch, getServerLocationSearch)
  const findingId = enabled ? new URLSearchParams(locationSearch).get(FINDING_QUERY_PARAM) : null
  const target = useMemo(
    () => (findingId ? getFindingTarget(findingId, openFindings, ignoredFindings, outdatedFindings) : null),
    [findingId, ignoredFindings, openFindings, outdatedFindings],
  )

  useEffect(() => {
    if (!enabled) {
      processedFindingRef.current = null
      return
    }

    if (!findingId || !target) {
      const wasShowingLinkedFinding = processedFindingRef.current !== null
      processedFindingRef.current = null
      if (wasShowingLinkedFinding) focusFindingsContainer()
      return
    }

    const processedFinding = processedFindingRef.current
    if (processedFinding?.findingId === findingId && processedFinding.view === target.view) {
      return
    }
    const shouldFocus = processedFinding?.findingId !== findingId
    processedFindingRef.current = {findingId, view: target.view}
    onExpandFinding(findingId)

    if (!shouldFocus) return

    return focusFindingToggle(findingId, {
      announcement: LINKED_FINDING_ANNOUNCEMENT,
    })
  }, [enabled, findingId, onExpandFinding, target])

  const clearLinkedFinding = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete(FINDING_QUERY_PARAM)
    updateSearchParams(searchParams)
  }, [])

  return {
    linkedFinding: target?.finding ?? null,
    linkedView: target?.view ?? null,
    clearLinkedFinding,
  }
}

const getFindingTarget = (
  findingId: string,
  openFindings: Finding[],
  ignoredFindings: Finding[],
  outdatedFindings: Finding[],
): {finding: Finding; view: FindingsView} | null => {
  const ignoredFinding = ignoredFindings.find(finding => finding.id === findingId)
  if (ignoredFinding) return {finding: ignoredFinding, view: 'resolved'}

  const outdatedFinding = outdatedFindings.find(finding => finding.id === findingId)
  if (outdatedFinding) return {finding: outdatedFinding, view: 'outdated'}

  const openFinding = openFindings.find(finding => finding.id === findingId)
  if (!openFinding) return null
  return {finding: openFinding, view: isNeedsAttention(openFinding) ? 'attention' : 'open'}
}
